import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { AcademyArea } from '../../../pages/academy/Academy/AcademyService';
import {
  getTierCompletionCount,
  isTierAvailable,
  isTierComplete,
} from '../Utilities/academyProgress';
import { TierBadge, type TierBadgeVariant } from '../TierBadge/TierBadge';
import { academyStyles } from '../../../pages/academy/Academy/Academy.styles';
import { tierProgressClassNames } from './TierProgress.styles';

export interface TierProgressProps {
  area: AcademyArea;
  selectedTierKey: string;
  onSelectTier: (tierKey: string) => void;
}

export function TierProgress({
  area,
  selectedTierKey,
  onSelectTier,
}: TierProgressProps) {
  const theme = useTheme();
  const isStacked = area.tiers.length > 2;

  return (
    <Box
      component="section"
      className={tierProgressClassNames.root}
      sx={academyStyles.tierSection()}
      aria-label="Tiers"
    >
      <Paper
        className={tierProgressClassNames.card}
        sx={academyStyles.tierCard(theme, isStacked)}
        aria-label={`${area.label} tiers`}
        elevation={0}
      >
        {area.tiers.map((tier, index) => {
          const isSelected = tier.tierKey === selectedTierKey;
          const isAvailable = isTierAvailable(area, index);
          const tierCompletion = getTierCompletionCount(area, tier.tierKey);
          const tierProgressText = `${tierCompletion.completedSkills} of ${tierCompletion.skillCount} skills complete`;

          return (
            <Button
              className={tierProgressClassNames.item}
              sx={academyStyles.tierItem(theme, isAvailable)}
              key={tier.tierKey}
              type="button"
              disableRipple
              disabled={!isAvailable}
              aria-pressed={isSelected}
              onClick={() => onSelectTier(tier.tierKey)}
            >
              <TierBadge variant={getTierBadgeVariant(index, isAvailable)} />
              <Box sx={academyStyles.tierCopy()}>
                <Box sx={academyStyles.tierHeadingRow()}>
                  <Typography component="h3" sx={academyStyles.tierHeading()}>
                    {tier.step}. {tier.name}
                  </Typography>
                  <Typography component="span" sx={academyStyles.tierInlineDescription()}>
                    {tier.description}
                  </Typography>
                </Box>
                <Box component="span" className="sr-only">
                  {getTierStateLabel(area, tier.tierKey, isSelected, isAvailable)}
                </Box>
              </Box>
              <LinearProgress
                sx={academyStyles.tierProgress(theme, tierCompletion.percent >= 100)}
                aria-label={`${tier.name} tier progress`}
                aria-valuetext={tierProgressText}
                variant="determinate"
                value={tierCompletion.percent}
              />
            </Button>
          );
        })}
      </Paper>
    </Box>
  );
}

function getTierStateLabel(
  area: AcademyArea,
  tierKey: string,
  isSelected: boolean,
  isAvailable: boolean,
): string {
  if (!isAvailable) {
    return 'Unavailable tier';
  }

  if (isSelected) {
    return 'Selected tier';
  }

  return isTierComplete(area, tierKey) ? 'Completed tier' : 'Available tier';
}

function getTierBadgeVariant(index: number, isAvailable: boolean): TierBadgeVariant {
  if (index === 0) {
    return 'silver';
  }

  return isAvailable ? 'gold' : 'unavailable';
}
